import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { observer } from "mobx-react";
import { useDocumentVisible } from "@/hooks/use-document-visible";
import { useTranslation } from "@/i18n";

const VERSION_URL = "/version.txt";
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const TOAST_ID = "app-version-update";

export async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: "no-store" });
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    // Guard against /version.txt being rewritten to index.html by a SPA
    // fallback (e.g. local dev w/o the file). SHAs are short hex, never HTML.
    if (!text || text.length > 64 || /[<\s]/.test(text)) return null;
    return text;
  } catch (err) {
    console.warn("[version-watcher] version poll failed", err);
    return null;
  }
}

interface UseVersionPollOptions {
  enabled: boolean;
  intervalMs?: number;
  onNewVersion: () => void;
}

export function useVersionPoll({
  enabled,
  intervalMs = POLL_INTERVAL_MS,
  onNewVersion,
}: UseVersionPollOptions) {
  const initialVersion = useRef<string | null>(null);
  const notified = useRef(false);
  const onNewVersionRef = useRef(onNewVersion);
  onNewVersionRef.current = onNewVersion;
  // A hidden tab stops polling; the effect re-runs when the tab is shown, so
  // the return is checked at once and the timer starts again.
  const visible = useDocumentVisible();

  useEffect(() => {
    if (!enabled || !visible) return;

    let cancelled = false;

    const check = async () => {
      const current = await fetchVersion();
      if (cancelled || current === null) return;
      if (initialVersion.current === null) {
        initialVersion.current = current;
        return;
      }
      if (current !== initialVersion.current && !notified.current) {
        notified.current = true;
        onNewVersionRef.current();
      }
    };

    void check();
    const interval = window.setInterval(check, intervalMs);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, visible, intervalMs]);
}

export const VersionWatcher = observer(function VersionWatcher() {
  const { t } = useTranslation();

  useVersionPoll({
    enabled: import.meta.env.PROD,
    onNewVersion: () => {
      toast(t("version_update.title"), {
        id: TOAST_ID,
        description: t("version_update.description"),
        duration: Infinity,
        action: {
          label: t("version_update.refresh"),
          onClick: () => window.location.reload(),
        },
      });
    },
  });

  return null;
});
